import React, { useMemo } from 'react';
import { SectionCard } from '../../components/sections/SectionCard';
import { DetailsList } from '../../components/display/DetailsList';
import { Button } from '@shared/components/ui/button';
import type { Terminal } from '@features/in-stores/types/terminal.types';
import type { PaymentDetail } from '../../types/panel-common.types';

interface TerminalConfigurationTabProps {
  terminal?: Terminal | null;
  className?: string;
}

const SECTION_SHADOW = 'shadow-[4px_8px_24px_0_rgba(11,59,69,0.08),0_1px_2px_0_rgba(11,59,69,0.06)]'
const SECTION_CARD = `rounded-2xl mb-0 ${SECTION_SHADOW}`

function buildPaymentConfig(): PaymentDetail[] {
  return [
    { label: 'Accepted Card Brands', value: 'Visa, Mastercard, Amex, Discover' },
    { label: 'Contactless Enabled', value: 'Yes' },
    { label: 'PIN Entry Required', value: 'Transactions > $50' },
    { label: 'Tip Support', value: 'Enabled (preset: 15%, 18%, 20%)' },
    { label: 'Offline Mode', value: 'Enabled (max $500 per transaction)' },
    { label: 'Currency', value: 'USD' },
    { label: 'Tax Calculation', value: 'Automatic (state-based)' },
  ];
}

function buildNetworkConfig(): PaymentDetail[] {
  return [
    { label: 'Primary Connection', value: 'Ethernet' },
    { label: 'Fallback Connection', value: 'Wi-Fi' },
    { label: 'VPN Enabled', value: 'Yes' },
    { label: 'TLS Version', value: '1.3' },
    { label: 'Heartbeat Interval', value: '30 seconds' },
    { label: 'Auto-reconnect', value: 'Enabled' },
  ];
}

function buildReceiptConfig(): PaymentDetail[] {
  return [
    { label: 'Print Receipts', value: 'Enabled' },
    { label: 'Email Receipts', value: 'Enabled' },
    { label: 'SMS Receipts', value: 'Disabled' },
    { label: 'Receipt Language', value: 'English' },
    { label: 'Custom Header', value: 'BRIKSTORE - Thank you!' },
    { label: 'Show Tip Line', value: 'Yes' },
  ];
}

function buildSecurityConfig(): PaymentDetail[] {
  return [
    { label: 'PCI Compliance Level', value: 'Level 1 (PCI PTS 5.x)' },
    { label: 'Encryption', value: 'P2PE (Point-to-Point Encryption)' },
    { label: 'Key Rotation', value: 'Every 24 hours' },
    { label: 'Tamper Detection', value: 'Active' },
    { label: 'Admin PIN Required', value: 'Yes' },
    { label: 'Auto-lock Timeout', value: '5 minutes' },
  ];
}

export const TerminalConfigurationTab = React.memo<TerminalConfigurationTabProps>(({
  className,
}) => {
  const paymentConfig = useMemo(() => buildPaymentConfig(), []);
  const networkConfig = useMemo(() => buildNetworkConfig(), []);
  const receiptConfig = useMemo(() => buildReceiptConfig(), []);
  const securityConfig = useMemo(() => buildSecurityConfig(), []);

  return (
    <div className={className}>
      <div className="flex flex-col gap-6">
        <SectionCard title="Payment Processing" layout="down" className={SECTION_CARD}
          headerActions={<Button variant="ghost" size="sm">Edit</Button>}>
          <DetailsList details={paymentConfig} isDetailView={true} />
        </SectionCard>

        <SectionCard title="Network" layout="down" className={SECTION_CARD}
          headerActions={<Button variant="ghost" size="sm">Edit</Button>}>
          <DetailsList details={networkConfig} isDetailView={true} />
        </SectionCard>

        <SectionCard title="Receipts" layout="down" className={SECTION_CARD}
          headerActions={<Button variant="ghost" size="sm">Edit</Button>}>
          <DetailsList details={receiptConfig} isDetailView={true} />
        </SectionCard>

        <SectionCard title="Security" layout="down" className={SECTION_CARD}
          headerActions={<Button variant="ghost" size="sm">Edit</Button>}>
          <DetailsList details={securityConfig} isDetailView={true} />
        </SectionCard>
      </div>
    </div>
  );
});

TerminalConfigurationTab.displayName = 'TerminalConfigurationTab';
